import type { Communication, PropositionDetails } from "@dune/types";
import {
	Button,
	PaginatedDataTable,
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from "@dune/ui";

import { Check, Copy, Mail } from "lucide-react";
import React, { useMemo, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import {
	useArchiveCommunicationProposition,
	useDeleteDraftEmail,
	useUpdateProcessedEmailProposition,
} from "../../../../api/mutations/mail.mutations";
import { useMe } from "../../../../api/queries/users.queries";
import { useEmailActions } from "../../../../stores/email.store";
import { propositionCommunicationsColumns } from "./communicationsColumns";
import { RoleEnum } from "../../../../../../../packages/vars/src";
import { generatePropositionEmailWithFallback } from "../../../../utils/emails.utils";

interface CommunicationsTabProps {
	proposition: PropositionDetails;
	communicationsResponse?: {
		data: Communication[];
		meta: {
			page: number;
			limit: number;
			total: number;
			totalPages: number;
			hasNextPage: boolean;
			hasPreviousPage: boolean;
		};
	};
	isLoading?: boolean;
	error?: unknown;
	onPageChange?: (page: number) => void;
	onSortChange?: (sortBy: string, sortOrder: "asc" | "desc") => void;
	onLimitChange?: (limit: number) => void;
}

export const CommunicationsTab: React.FC<CommunicationsTabProps> = ({
	proposition,
	communicationsResponse,
	isLoading = false,
	error,
	onPageChange = () => {},
	onSortChange = () => {},
	onLimitChange = () => {},
}) => {
	const { selectEmail, openComposer } = useEmailActions();
	const [copied, setCopied] = useState(false);
	const { me } = useMe();
	const canCompose = me?.role === RoleEnum.DUNE;

	const deleteDraftMutation = useDeleteDraftEmail({
		entityId: proposition.id,
		entityType: "PROPOSITION",
	});

	const updateProcessedMutation = useUpdateProcessedEmailProposition(
		proposition.id,
	);
	const archiveMutation = useArchiveCommunicationProposition(proposition.id);

	const communications = communicationsResponse?.data || [];
	const meta = communicationsResponse?.meta ?? {
		page: 1,
		limit: 10,
		total: 0,
		totalPages: 0,
		hasNextPage: false,
		hasPreviousPage: false,
	};

	const propositionEmail = generatePropositionEmailWithFallback(
		proposition.emailRoutingUuid,
		proposition.id,
	);

	const handleCopyEmail = async () => {
		await navigator.clipboard.writeText(propositionEmail);
		setCopied(true);
		setTimeout(() => setCopied(false), 1500);
	};

	const handleNewMail = () => {
		openComposer({ type: "new" });
	};

	const handleRowClick = (mail: Communication) => {
		if (mail.status === "DRAFT" && canCompose) {
			openComposer({
				type: "new",
				initialData: {
					to: mail.to.map((email) => ({
						value: email,
						label: email,
					})),
					cc: [],
					subject: mail.subject,
					content: mail.htmlContent,
					attachments:
						mail.attachments?.map((att) => ({
							id: uuidv4(),
							file: new File([], att.fileName, {
								type: att.contentType,
							}),
							name: att.fileName,
							size: att.size,
							type: att.contentType,
							url: att.url,
							contentType: att.contentType,
							isUploading: false,
						})) ?? [],
				},
				draftId: mail.id.toString(),
			});
		} else {
			selectEmail(mail.id.toString());
		}
	};

	const columns = useMemo(
		() =>
			propositionCommunicationsColumns({
				onUpdateProcessed: updateProcessedMutation,
				onArchive: archiveMutation,
				onDeleteDraft: deleteDraftMutation,
			}),
		[updateProcessedMutation, archiveMutation, deleteDraftMutation],
	);

	if (error) return <div>Erreur lors du chargement des communications.</div>;

	return (
		<div className="h-full">
			<div className="flex items-center justify-between mb-2 w-full">
				<h2 className="text-lg text-text-main-blue font-semibold">
					Communications
				</h2>
				<div className="flex w-full max-w-xs justify-end gap-2">
					<Tooltip>
						<TooltipTrigger asChild>
							<Button
								type="button"
								variant="outline"
								size="icon"
								onClick={handleCopyEmail}
								aria-label={
									copied ? "Email copié !" : `Copier l'email proposition`
								}
							>
								{copied ? (
									<Check size={18} className="text-green-600" />
								) : (
									<Copy size={18} />
								)}
							</Button>
						</TooltipTrigger>
						<TooltipContent sideOffset={8}>
							{copied ? "Email copié !" : `Copier l'email: ${propositionEmail}`}
						</TooltipContent>
					</Tooltip>
					{canCompose && (
						<Button type="button" variant="default" onClick={handleNewMail}>
							<Mail size={16} className="mr-2" />
							Nouveau mail
						</Button>
					)}
				</div>
			</div>
			<PaginatedDataTable
				data={communications}
				meta={meta}
				columns={columns}
				onPageChange={onPageChange}
				onSortChange={onSortChange}
				onLimitChange={onLimitChange}
				noResultsText="Aucune communication trouvée"
				isLoading={isLoading}
				onRowClick={handleRowClick}
				variant="static-clean"
				enableSorting={true}
			/>
		</div>
	);
};
